/**
 * 67.78: an autocannon, voiced — the rules and the guns' table are `autocannonMix.ts` (`CANNON_VOICES`: point defence's two
 * mounts first; a vehicle's chain gun later). One looping voice a gun (two a base), alive while it spins or fires and its
 * echo rings, let go after. Synthesized (no files):
 *   - **the rounds:** noise through a band at the cracks' pitch, opened once a round by a pulse (a sawtooth at the rate
 *     through a wave-shaper: a sharp attack, a quick decay) — the "brrrt";
 *   - **the growl:** a sawtooth at the rate through a low-pass (the cannon's pitch), and the muzzle blast's roar, low noise;
 *   - **the echo:** low noise rolling on after a burst, released over the heard tail;
 *   - **the spin** (a rotary gun): the barrel cluster's motor, a triangle through a band at the whine's pitch;
 *   - the firing gate and the spin move at the context's time plus the speed-of-sound delay, so a far mount is heard late.
 * A self-destructing round's pop is a one-shot (`playCannonPop`). Cost: a gun's voice 4 oscillators, 4 noise sources and a
 * wave-shaper, ~25 nodes, while it spins or rings; a frame, ~10 parameter updates; a pop 8 nodes for 0.25 s.
 * Presentation only.
 */
import { getAudioContext, getSfxBus, initAudio } from './sfx';
import type { CannonHeard, CannonVoice } from './autocannonMix';

export interface CannonOut { ctx: BaseAudioContext; dest: AudioNode }

const noiseBufs = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseBuf(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseBufs.get(ctx);
  if (!b) {
    const len = ctx.sampleRate * 2; b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    noiseBufs.set(ctx, b);
  }
  return b;
}
/** A round's envelope from the rate's sawtooth (−1 … 1, rising): full as each cycle begins, falling fast through it. */
const pulses = new WeakMap<BaseAudioContext, Float32Array<ArrayBuffer>>();
function pulseCurve(ctx: BaseAudioContext): Float32Array<ArrayBuffer> {
  let c = pulses.get(ctx);
  if (!c) {
    const n = 1024; c = new Float32Array(new ArrayBuffer(n * 4));
    for (let i = 0; i < n; i++) { const x = i / (n - 1); c[i] = (1 - x) ** 7; }
    pulses.set(ctx, c);
  }
  return c;
}

interface Voice {
  out: GainNode; air: BiquadFilterNode; side: StereoPannerNode;
  rate: OscillatorNode; growl: OscillatorNode; fire: GainNode; echo: GainNode;
  whine: OscillatorNode; whineBand: BiquadFilterNode; whineGain: GainNode;
  sources: AudioScheduledSourceNode[];
}

/** What the voice is told each frame: how it is heard, and the gun's state there (firing, the whine's pitch and level, the rate). */
export interface CannonVoiceState { heard: CannonHeard; firing: boolean; whineHz: number; whineLevel: number; rate: number }

export class AutocannonVoice {
  private v: Voice | null = null;
  private firing = false;
  /** × its loudness (a host's volume). */
  volume = 1;
  constructor(private readonly gun: CannonVoice, private readonly out?: CannonOut) {}

  get live(): boolean { return this.v !== null; }

  /** This frame's state (null: silent — the echo rings out and the voice is let go). */
  update(s: CannonVoiceState | null): void {
    const where = this.where();
    if (!where) return;
    const now = where.ctx.currentTime;
    if (!s) { if (this.v) { this.release(this.v, now); this.v = null; this.firing = false; } return; }
    const v = (this.v ??= this.voice(where.ctx, where.dest));
    const h = s.heard, t = now + h.delay;   // (the gun's changes reach the listener late)
    v.rate.frequency.setTargetAtTime(s.rate, t, 0.05);
    v.growl.frequency.setTargetAtTime(s.rate, t, 0.05);
    if (s.firing !== this.firing) {
      v.fire.gain.setTargetAtTime(s.firing ? 1 : 0, t, s.firing ? 0.008 : 0.02);
      v.echo.gain.setTargetAtTime(s.firing ? 0.32 : 0, s.firing ? t : t + 0.05, s.firing ? 0.15 : h.tail / 3);
      this.firing = s.firing;
    }
    if (s.whineHz > 0) { v.whine.frequency.setTargetAtTime(s.whineHz, t, 0.1); v.whineBand.frequency.setTargetAtTime(s.whineHz, t, 0.1); }
    v.whineGain.gain.setTargetAtTime(s.whineLevel, t, 0.1);
    v.air.frequency.setTargetAtTime(h.cutoff, now, 0.2);
    v.side.pan.setTargetAtTime(h.pan, now, 0.1);
    v.out.gain.setTargetAtTime(h.gain * this.volume, now, 0.1);
  }

  stop(): void { this.update(null); }

  private where(): CannonOut | null {
    if (this.out) return this.out;
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    return ctx && bus && ctx.state !== 'closed' ? { ctx, dest: bus } : null;
  }

  private release(v: Voice, t: number): void {
    v.fire.gain.setTargetAtTime(0, t, 0.02);
    v.out.gain.setTargetAtTime(0, t + 0.5, 0.4);
    for (const s of v.sources) { try { s.stop(t + 2.5); } catch { /* already stopped */ } }
  }

  private voice(ctx: BaseAudioContext, dest: AudioNode): Voice {
    const G = this.gun, whineRest = G.rotary?.whineRest ?? 300;
    const sources: AudioScheduledSourceNode[] = [];
    const gain = (v: number) => { const g = ctx.createGain(); g.gain.value = v; return g; };
    const filter = (type: BiquadFilterType, hz: number, q: number) => { const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = hz; f.Q.value = q; return f; };
    const osc = (type: OscillatorType, hz: number) => { const o = ctx.createOscillator(); o.type = type; o.frequency.value = hz; sources.push(o); return o; };
    const noise = () => { const s = ctx.createBufferSource(); s.buffer = noiseBuf(ctx); s.loop = true; s.loopStart = Math.random(); sources.push(s); return s; };
    const out = gain(0), air = filter('lowpass', 14000, 0.5), side = ctx.createStereoPanner();
    air.connect(side); side.connect(out); out.connect(dest);
    // the firing gate: everything the rounds make goes through it
    const fire = gain(0);
    fire.connect(air);
    // the rounds: a crack a round — noise in a band, opened by the pulse
    const rate = osc('sawtooth', G.rate), shaper = ctx.createWaveShaper(); shaper.curve = pulseCurve(ctx);
    const crackSrc = noise(), crackBand = filter('bandpass', G.crackHz, 0.8), crackVca = gain(0), crackLevel = gain(1.3);
    rate.connect(shaper); shaper.connect(crackVca.gain);
    crackSrc.connect(crackBand); crackBand.connect(crackVca); crackVca.connect(crackLevel); crackLevel.connect(fire);
    // the thump a round, low: the same pulse on low noise
    const thumpSrc = noise(), thumpLow = filter('lowpass', 260, 0.8), thumpVca = gain(0), thumpLevel = gain(1.6);
    shaper.connect(thumpVca.gain);
    thumpSrc.connect(thumpLow); thumpLow.connect(thumpVca); thumpVca.connect(thumpLevel); thumpLevel.connect(fire);
    // the growl at the rate's pitch, and the muzzle blast's roar
    const growl = osc('sawtooth', G.rate), growlLow = filter('lowpass', 420, 0.9), growlLevel = gain(G.growl);
    growl.connect(growlLow); growlLow.connect(growlLevel); growlLevel.connect(fire);
    const roarSrc = noise(), roarBand = filter('bandpass', G.roarHz, 0.6), roarLevel = gain(0.35);
    roarSrc.connect(roarBand); roarBand.connect(roarLevel); roarLevel.connect(fire);
    // the echo: low noise rolling on after the gate shuts (its own gain, released slowly)
    const echoSrc = noise(), echoLow = filter('lowpass', 480, 0.7), echo = gain(0);
    echoSrc.connect(echoLow); echoLow.connect(echo); echo.connect(air);
    // the spin: the barrels' motor
    const whine = osc('triangle', whineRest), whineBand = filter('bandpass', whineRest, 4), whineGain = gain(0);   // (silent on a chain gun: its level stays 0)
    whine.connect(whineBand); whineBand.connect(whineGain); whineGain.connect(air);
    for (const s of sources) s.start();
    return { out, air, side, rate, growl, fire, echo, whine, whineBand, whineGain, sources };
  }
}

/** A self-destructing round's pop: a sharp small crack and a short thud, as heard (`h`), at `when` (the context's seconds; its delay added). Returns its nodes. */
export function playCannonPop(ctx: BaseAudioContext, dest: AudioNode, h: CannonHeard, when: number): number {
  const t = when + h.delay;
  const g = ctx.createGain(); g.gain.value = h.gain;
  const air = ctx.createBiquadFilter(); air.type = 'lowpass'; air.frequency.value = h.cutoff;
  const side = ctx.createStereoPanner(); side.pan.value = h.pan;
  g.connect(air); air.connect(side); side.connect(dest);
  const src = ctx.createBufferSource(); src.buffer = noiseBuf(ctx);
  const band = ctx.createBiquadFilter(); band.type = 'bandpass'; band.frequency.value = 2100; band.Q.value = 0.9;
  const env = ctx.createGain();
  env.gain.setValueAtTime(0, t); env.gain.linearRampToValueAtTime(1, t + 0.002); env.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
  const low = ctx.createBiquadFilter(); low.type = 'lowpass'; low.frequency.value = 520;
  const env2 = ctx.createGain();
  env2.gain.setValueAtTime(0, t); env2.gain.linearRampToValueAtTime(0.7, t + 0.004); env2.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
  src.connect(band); band.connect(env); env.connect(g);
  src.connect(low); low.connect(env2); env2.connect(g);
  src.start(t, Math.random(), 0.25);
  src.onended = () => { for (const n of [src, band, env, low, env2, g, air, side]) n.disconnect(); };
  return 8;
}

/** A pop into the game's SFX bus now (its delay added); nothing when the audio is off. */
export function playCannonPopHere(h: CannonHeard): void {
  initAudio();
  const ctx = getAudioContext(), bus = getSfxBus();
  if (ctx && bus && ctx.state !== 'closed') playCannonPop(ctx, bus, h, ctx.currentTime + 0.01);
}
